import { parseHHmm } from './tz.js';

/** [min, max] in minutes. */
export type MinRange = readonly [min: number, max: number];

/** Wake windows for a schedule: first (morning), middle (between naps), last (before bedtime). */
export interface WakeWindowSet {
  first: MinRange;
  middle: MinRange;
  last: MinRange;
}

export interface AgeBand {
  /** Band starts at this age in weeks (inclusive); values are anchors interpolated toward the next band. */
  fromWeek: number;
  label: string;
  /** Null when no daytime nap is typical. */
  wakeWindows: WakeWindowSet | null;
  naps: { typical: number; min: number; max: number };
  napLengthMin: MinRange;
  daySleepMin: MinRange;
  nightSleepMin: MinRange;
  /** Total sleep per 24 h (AAP/NSF recommendation ranges). */
  totalSleepMin: MinRange;
  /** Typical bedtime window, local 'HH:mm'. */
  bedtime: readonly [earliest: string, latest: string];
  source: string;
}

// ---------------------------------------------------------------------------
// AGE NORMS TABLE
// !!! General, publicly available pediatric sleep guidance (AAP / National Sleep
// !!! Foundation total-sleep ranges + commonly cited wake-window ranges). These
// !!! numbers are NOT medical advice. Reviewed 2026-10-26 (emulated expert review,
// !!! see docs/expert-review-sleep.md): totals follow AASM/AAP 2016 and NSF 2015;
// !!! day-sleep and nap-count ranges are checked against Iglowstein 2003 (Zurich,
// !!! Pediatrics 111:302) and Staton 2020. Bedtime windows unchanged (see review).
// !!! WAKE WINDOWS ARE PRACTICE-BASED (no peer-reviewed reference values exist):
// !!! treat them as starting points that the baby's own history overrides.
// !!! Edit here only; everything else derives from it.
// ---------------------------------------------------------------------------
export const AGE_BANDS: readonly AgeBand[] = [
  {
    fromWeek: 0,
    label: 'Newborn (0–5 weeks)',
    wakeWindows: { first: [35, 50], middle: [40, 60], last: [45, 60] },
    naps: { typical: 5, min: 4, max: 7 },
    napLengthMin: [20, 120],
    daySleepMin: [360, 540],
    nightSleepMin: [480, 540],
    totalSleepMin: [840, 1020],
    bedtime: ['20:30', '23:00'],
    source: 'NSF 2015: 0–3 mo 14–17 h total; commonly cited newborn wake windows 45–60 min; no real schedule yet.',
  },
  {
    fromWeek: 6,
    label: '6–11 weeks',
    wakeWindows: { first: [50, 70], middle: [60, 80], last: [60, 90] },
    naps: { typical: 4, min: 3, max: 6 },
    napLengthMin: [30, 120],
    daySleepMin: [300, 480],
    nightSleepMin: [540, 600],
    totalSleepMin: [840, 1020],
    bedtime: ['20:00', '22:30'],
    source: 'NSF 2015: 14–17 h total; practice-based wake windows 60–90 min; 3–5 naps (3 long naps is a normal variant).',
  },
  {
    fromWeek: 12,
    label: '~3 months',
    wakeWindows: { first: [75, 90], middle: [80, 105], last: [90, 120] },
    naps: { typical: 4, min: 3, max: 5 },
    napLengthMin: [30, 120],
    daySleepMin: [240, 360],
    nightSleepMin: [600, 660],
    totalSleepMin: [840, 1020],
    bedtime: ['19:30', '21:00'],
    source: 'NSF 2015: 14–17 h total; commonly cited wake windows 75–120 min around 3 months.',
  },
  {
    fromWeek: 17,
    label: '~4–5 months',
    wakeWindows: { first: [90, 120], middle: [105, 135], last: [120, 150] },
    naps: { typical: 3, min: 3, max: 4 },
    napLengthMin: [30, 120],
    daySleepMin: [180, 270],
    nightSleepMin: [600, 720],
    totalSleepMin: [720, 960],
    bedtime: ['18:30', '20:30'],
    source: 'AAP (2016 consensus): 4–12 mo 12–16 h incl. naps; commonly cited wake windows 1.5–2.5 h; 4→3 naps.',
  },
  {
    fromWeek: 26,
    label: '~6–7 months',
    wakeWindows: { first: [120, 150], middle: [135, 165], last: [150, 180] },
    naps: { typical: 3, min: 2, max: 3 },
    napLengthMin: [45, 120],
    daySleepMin: [150, 240],
    nightSleepMin: [630, 720],
    totalSleepMin: [720, 960],
    bedtime: ['18:30', '20:00'],
    source: 'AASM/AAP 2016: 12–16 h; Iglowstein 2003: day sleep 3.4±1.5 h at 6 mo; practice-based wake windows 2–3 h; 3→2 naps typically 6–9 mo.',
  },
  {
    fromWeek: 34,
    label: '~8 months',
    wakeWindows: { first: [135, 165], middle: [150, 180], last: [165, 210] },
    naps: { typical: 2, min: 2, max: 3 },
    napLengthMin: [45, 120],
    daySleepMin: [120, 210],
    nightSleepMin: [660, 720],
    totalSleepMin: [720, 960],
    bedtime: ['18:30', '20:00'],
    source: 'AASM/AAP 2016: 12–16 h; Iglowstein 2003: day sleep 3.4 h (6 mo) → 2.8 h (9 mo); practice-based 2-nap wake windows 2.25–3.5 h.',
  },
  {
    fromWeek: 39,
    label: '~9–11 months',
    wakeWindows: { first: [150, 180], middle: [165, 195], last: [180, 210] },
    naps: { typical: 2, min: 2, max: 3 },
    napLengthMin: [60, 120],
    daySleepMin: [120, 210],
    nightSleepMin: [660, 720],
    totalSleepMin: [720, 960],
    bedtime: ['18:30', '20:00'],
    source: 'AASM/AAP 2016: 12–16 h; Iglowstein 2003: day sleep 2.8±1.2 h at 9 mo, night 11.2 h; practice-based wake windows 2.5–3.5 h; some still take 3 naps at 9 mo.',
  },
  {
    fromWeek: 52,
    label: '~12–14 months',
    wakeWindows: { first: [180, 210], middle: [195, 225], last: [210, 240] },
    naps: { typical: 2, min: 1, max: 2 },
    napLengthMin: [60, 120],
    daySleepMin: [120, 180],
    nightSleepMin: [660, 720],
    totalSleepMin: [660, 840],
    bedtime: ['18:30', '20:00'],
    source: 'AASM/AAP 2016: 1–2 y 11–14 h incl. naps; Iglowstein 2003: day sleep 2.4±1.1 h at 12 mo; practice-based wake windows 3–4 h.',
  },
  {
    fromWeek: 65,
    label: '~15–17 months',
    wakeWindows: { first: [195, 240], middle: [210, 240], last: [225, 270] },
    naps: { typical: 2, min: 1, max: 2 },
    napLengthMin: [60, 120],
    daySleepMin: [105, 165],
    nightSleepMin: [660, 720],
    totalSleepMin: [660, 840],
    bedtime: ['18:45', '20:15'],
    source: 'AASM/AAP 2016: 11–14 h; 2→1 nap transition commonly 13–18 mo (Iglowstein 2003: significant shift to 1 nap at 18 mo).',
  },
  {
    fromWeek: 78,
    label: '~18–23 months',
    wakeWindows: { first: [270, 330], middle: [270, 330], last: [240, 330] },
    naps: { typical: 1, min: 1, max: 2 },
    napLengthMin: [90, 150],
    daySleepMin: [90, 150],
    nightSleepMin: [660, 720],
    totalSleepMin: [660, 840],
    bedtime: ['19:00', '20:30'],
    source: 'AASM/AAP 2016: 11–14 h; Iglowstein 2003: day sleep 2.0±0.7 h at 18 mo, 96% nap; ~1 in 4 still on 2 naps at 18 mo (Weissbluth); practice-based wake windows 4.5–6 h.',
  },
  {
    fromWeek: 104,
    label: '~2 years',
    wakeWindows: { first: [300, 360], middle: [300, 360], last: [270, 360] },
    naps: { typical: 1, min: 0, max: 1 },
    napLengthMin: [60, 150],
    daySleepMin: [60, 150],
    nightSleepMin: [630, 720],
    totalSleepMin: [660, 840],
    bedtime: ['19:00', '20:30'],
    source: 'AASM/AAP 2016: 1–2 y 11–14 h; Iglowstein 2003: 87% nap at 2 y, 1.8±0.5 h; Staton 2020: <2.5% stop napping before 2 y; practice-based wake windows 5–6 h.',
  },
  {
    fromWeek: 130,
    label: '~2.5 years',
    wakeWindows: { first: [315, 375], middle: [315, 375], last: [300, 390] },
    naps: { typical: 1, min: 0, max: 1 },
    napLengthMin: [60, 120],
    daySleepMin: [60, 120],
    nightSleepMin: [630, 720],
    totalSleepMin: [660, 840],
    bedtime: ['19:00', '20:30'],
    source: 'AASM/AAP 2016: 11–14 h until 3 y; nap often shortens; practice-based wake windows 5–6.5 h.',
  },
  {
    fromWeek: 156,
    label: '3 years+',
    wakeWindows: null,
    naps: { typical: 0, min: 0, max: 1 },
    napLengthMin: [60, 120],
    daySleepMin: [0, 120],
    nightSleepMin: [600, 720],
    totalSleepMin: [600, 780],
    bedtime: ['19:00', '20:30'],
    source: 'AASM/AAP 2016: 3–5 y 10–13 h incl. naps; about half of 3-year-olds still nap (Iglowstein 2003: 50%, 1.7±0.4 h); most stop between 3 and 5 y (Staton 2020). Typical 0 is a coin flip: history decides.',
  },
];

/** Up to this age, schedules are fluid: wake windows interpolate even across nap-count changes. */
export const FLUID_SCHEDULE_UNTIL_WEEK = 17;
/** Awake time for a no-nap day (wake -> bedtime), used when napCount = 0. */
export const NO_NAP_DAY_WINDOW: MinRange = [600, 780];

export interface AgeNorms {
  ageWeeks: number;
  band: AgeBand;
  nextBand: AgeBand | null;
  wakeWindows: WakeWindowSet | null;
  naps: { typical: number; min: number; max: number };
  napLengthMin: MinRange;
  daySleepMin: MinRange;
  nightSleepMin: MinRange;
  totalSleepMin: MinRange;
  /** Bedtime window as local minutes of day. */
  bedtimeMin: MinRange;
}

const round5 = (n: number): number => Math.round(n / 5) * 5;
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpRange = (a: MinRange, b: MinRange, t: number): MinRange => [round5(lerp(a[0], b[0], t)), round5(lerp(a[1], b[1], t))];
const bedtimeRange = (b: AgeBand): MinRange => [parseHHmm(b.bedtime[0]), parseHHmm(b.bedtime[1])];

function bandIndexFor(weeks: number): number {
  let idx = 0;
  for (let i = 0; i < AGE_BANDS.length; i++) {
    const b = AGE_BANDS[i];
    if (b && b.fromWeek <= weeks) idx = i;
  }
  return idx;
}

function bandAt(i: number): AgeBand {
  const b = AGE_BANDS[i];
  if (!b) throw new RangeError(`No age band at index ${i}`);
  return b;
}

function canInterpolateWindows(a: AgeBand, b: AgeBand): boolean {
  return a.naps.typical === b.naps.typical || a.fromWeek < FLUID_SCHEDULE_UNTIL_WEEK;
}

/** Age norms for `ageDays` (use AgeInfo.ageForSleepDays), linearly interpolated between bands. */
export function getAgeNorms(ageDays: number): AgeNorms {
  const weeks = Math.max(0, ageDays) / 7;
  const i = bandIndexFor(weeks);
  const band = bandAt(i);
  const nextBand = AGE_BANDS[i + 1] ?? null;
  const t = nextBand ? Math.min(1, (weeks - band.fromWeek) / (nextBand.fromWeek - band.fromWeek)) : 0;
  const ir = (pick: (b: AgeBand) => MinRange): MinRange => (nextBand ? lerpRange(pick(band), pick(nextBand), t) : pick(band));

  let wakeWindows = band.wakeWindows;
  if (wakeWindows && nextBand?.wakeWindows && canInterpolateWindows(band, nextBand)) {
    const n = nextBand.wakeWindows;
    wakeWindows = {
      first: lerpRange(wakeWindows.first, n.first, t),
      middle: lerpRange(wakeWindows.middle, n.middle, t),
      last: lerpRange(wakeWindows.last, n.last, t),
    };
  }
  return {
    ageWeeks: weeks,
    band,
    nextBand,
    wakeWindows,
    naps: band.naps,
    napLengthMin: ir((b) => b.napLengthMin),
    daySleepMin: ir((b) => b.daySleepMin),
    nightSleepMin: ir((b) => b.nightSleepMin),
    totalSleepMin: ir((b) => b.totalSleepMin),
    bedtimeMin: nextBand ? lerpRange(bedtimeRange(band), bedtimeRange(nextBand), t) : bedtimeRange(band),
  };
}

/** Expand first/middle/last into napCount + 1 windows (index 0 = morning, last = before bedtime). */
export function windowsForNapCount(set: WakeWindowSet, napCount: number): MinRange[] {
  if (napCount <= 0) return [NO_NAP_DAY_WINDOW];
  const out: MinRange[] = [set.first];
  for (let i = 1; i < napCount; i++) out.push(set.middle);
  out.push(set.last);
  return out;
}

export interface ScheduleNorms {
  napCount: number;
  /** napCount + 1 wake windows. */
  windows: MinRange[];
  napLengthMin: MinRange;
  daySleepMin: MinRange;
  /** True if the norms come from the baby's own age band (false = borrowed from the nearest band with this nap count). */
  matchesAge: boolean;
  /** Band the windows were taken from. */
  sourceBand: AgeBand;
}

/**
 * Wake windows for a specific nap count. If the baby's nap count differs from what is typical
 * for the age (e.g. still 3 naps at 8 months, or already 1 nap at 14 months), the windows are
 * borrowed from the nearest age band where that nap count is typical.
 */
export function getScheduleNorms(ageDays: number, napCount: number): ScheduleNorms {
  const norms = getAgeNorms(ageDays);
  const fluid = norms.ageWeeks < FLUID_SCHEDULE_UNTIL_WEEK;
  if (napCount === 0) {
    return {
      napCount: 0,
      windows: [NO_NAP_DAY_WINDOW],
      napLengthMin: norms.napLengthMin,
      daySleepMin: [0, 0],
      matchesAge: norms.naps.typical === 0,
      sourceBand: norms.band,
    };
  }
  if (norms.wakeWindows && (norms.naps.typical === napCount || fluid)) {
    return {
      napCount,
      windows: windowsForNapCount(norms.wakeWindows, napCount),
      napLengthMin: norms.napLengthMin,
      daySleepMin: norms.daySleepMin,
      matchesAge: norms.naps.typical === napCount,
      sourceBand: norms.band,
    };
  }
  // Nearest band (by age distance) whose typical nap count equals napCount; else closest count.
  let best: AgeBand | null = null;
  let bestScore = Infinity;
  for (const b of AGE_BANDS) {
    if (!b.wakeWindows) continue;
    const score = Math.abs(b.naps.typical - napCount) * 1000 + Math.abs(b.fromWeek - norms.ageWeeks);
    if (score < bestScore) {
      bestScore = score;
      best = b;
    }
  }
  const src = best ?? norms.band;
  const set = src.wakeWindows ?? { first: NO_NAP_DAY_WINDOW, middle: NO_NAP_DAY_WINDOW, last: NO_NAP_DAY_WINDOW };
  return {
    napCount,
    windows: windowsForNapCount(set, napCount),
    napLengthMin: src.napLengthMin,
    daySleepMin: src.daySleepMin,
    matchesAge: false,
    sourceBand: src,
  };
}

export const midpoint = (r: MinRange): number => (r[0] + r[1]) / 2;
